import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import {
  enableDisableRadioButtonOptions,
  isEnabledValue,
  toEnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  APPLICATION_ANSWER_EVIDENCE_ENABLED_LABEL,
  NATIONALITY_SCRUTINY_ENABLED_LABEL,
  SHORTLISTING_ENABLED_LABEL,
  SHORTLISTING_DATA_TEST_ID,
  SHORTLISTING_SECTION_TITLE,
} from './ShortlistingSection.constants';
import styles from './ShortlistingSection.module.scss';

type ShortlistingSectionFormValues = {
  shortlistingEnabled: string;
  nationalityScrutinyEnabled: string;
  applicationAnswerEvidenceEnabled: string;
};

type ShortlistingSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const ShortlistingSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: ShortlistingSectionFormProps) => {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: ShortlistingSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isShortlistingEnabled: isEnabledValue(values.shortlistingEnabled),
        isAiShortlistNationalityScrutinyEnabled: isEnabledValue(
          values.nationalityScrutinyEnabled
        ),
        isApplicationAnswerEvidenceEnabled: isEnabledValue(
          values.applicationAnswerEvidenceEnabled
        ),
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Shortlisting settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Shortlisting settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    shortlistingEnabled: toEnableDisableValue(company.isShortlistingEnabled),
    nationalityScrutinyEnabled: toEnableDisableValue(
      company.isAiShortlistNationalityScrutinyEnabled
    ),
    applicationAnswerEvidenceEnabled: toEnableDisableValue(
      company.isApplicationAnswerEvidenceEnabled
    ),
  };

  return (
    <Formik<ShortlistingSectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title={SHORTLISTING_SECTION_TITLE}
          formId="shortlisting-form"
          hasResetAllButton={false}
          data-testid={SHORTLISTING_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="shortlisting-form-element"
            className={styles.form}
          >
            <FormikRadioButtonGroup
              id="shortlistingEnabled"
              name="shortlistingEnabled"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={SHORTLISTING_ENABLED_LABEL}
              data-testid={`${SHORTLISTING_DATA_TEST_ID}__available-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <FormikRadioButtonGroup
              id="nationalityScrutinyEnabled"
              name="nationalityScrutinyEnabled"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={NATIONALITY_SCRUTINY_ENABLED_LABEL}
              data-testid={`${SHORTLISTING_DATA_TEST_ID}__nationality-scrutiny-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <FormikRadioButtonGroup
              id="applicationAnswerEvidenceEnabled"
              name="applicationAnswerEvidenceEnabled"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={APPLICATION_ANSWER_EVIDENCE_ENABLED_LABEL}
              data-testid={`${SHORTLISTING_DATA_TEST_ID}__application-answer-evidence-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default ShortlistingSectionForm;
